/**
 * Связка двух Swiper + воспроизведение видео в карточках постов-сетки.
 * На мобилке (≤781px) слайдер отключён: 2 карточки + «Загрузить еще» по 2.
 */
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

const PCM_MOBILE_MAX = 781;
const PCM_MOBILE_INITIAL = 2;
const PCM_MOBILE_STEP = 2;

/**
 * @param {string | undefined} raw
 * @return {Record<string, unknown> | null}
 */
function parseConfig(raw) {
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function pcmViewportWidth() {
	return typeof window !== 'undefined' ? window.innerWidth : 1025;
}

function isPcmMobileViewport() {
	return pcmViewportWidth() <= PCM_MOBILE_MAX;
}

/**
 * Логическое число «ячеек» для minIndex / навигации (Swiper с slidesPerView: auto ориентируется по ширине слайда в CSS).
 *
 * @param {number} slideTotal
 * @param {Record<string, unknown>} cfg
 * @return {number}
 */
function pcmLogicalThumbSpv(slideTotal, cfg) {
	const vw = pcmViewportWidth();
	const capSlides = Math.max(1, slideTotal - 1);
	const tablet = typeof cfg.thumbsTablet === 'number' ? cfg.thumbsTablet : 2;
	const desktop = typeof cfg.thumbsDesktop === 'number' ? cfg.thumbsDesktop : 2;
	if (vw <= 781) {
		return 1;
	}
	if (vw <= 1200) {
		return Math.min(tablet, capSlides);
	}
	return Math.min(desktop, capSlides);
}

/**
 * Проверка: нужно ли вообще показывать стрелки на текущем брейкпоинте.
 * На мобилке main скрыт → учитываем все slideTotal слайдов.
 * На десктопе/планшете первый слайд в main → учитываем slideTotal - 1.
 *
 * @param {number} slideTotal
 * @param {Record<string, unknown>} cfg
 * @return {boolean}
 */
function pcmThumbsNeedNav(slideTotal, cfg) {
	const isMobile = pcmViewportWidth() <= 781;
	const visibleSlides = isMobile ? slideTotal : Math.max(1, slideTotal - 1);
	return visibleSlides > pcmLogicalThumbSpv(slideTotal, cfg);
}

/**
 * Минимальный activeIndex малых слайдов: смещение на 1, если по ширине помещается «хвост» после большого.
 * На мобилке (≤781px) main-слайдер скрыт, поэтому начинаем с 0.
 *
 * @param {number} slideTotal
 * @param {number} spv
 * @return {number}
 */
function getMinThumbIndex(slideTotal, spv) {
	if (pcmViewportWidth() <= 781) {
		return 0;
	}
	if (slideTotal < 2) {
		return 0;
	}
	if (slideTotal === 2) {
		return 1;
	}
	return slideTotal > spv ? 1 : 0;
}

/**
 * Большой слайдер следует за малым: activeIndex малых = t → большой показывает t - 1.
 * На мобилке (≤781px) main-слайдер скрыт, синхронизация не нужна.
 *
 * @param {Swiper} thumbs
 * @param {Swiper} main
 * @param {number} speed
 */
function syncMainFromThumbs(thumbs, main, speed = 0) {
	if (pcmViewportWidth() <= 781) {
		return;
	}
	let m = thumbs.activeIndex - 1;
	if (m < 0) {
		m = 0;
	}
	const maxM = Math.max(0, main.slides.length - 1);
	if (m > maxM) {
		m = maxM;
	}
	if (main.activeIndex !== m) {
		main.slideTo(m, speed);
	}
}

/**
 * @param {Swiper} thumbs
 * @return {number}
 */
function getPcmTransitionMs(thumbs) {
	const s = thumbs.params.speed;
	return typeof s === 'number' && s >= 0 ? s : 400;
}

/**
 * Стрелки привязаны к малым слайдам: next «заканчивается» вместе с ними; prev — с учётом minThumbIndex.
 *
 * @param {Swiper} thumbs
 * @param {HTMLElement | null} root
 * @param {string} sliderId
 * @param {boolean} enableNav
 */
function updatePCMNavButtons(thumbs, root, sliderId, enableNav, slideTotal, cfg) {
	if (!enableNav || !root) {
		return;
	}
	const prevBtn = root.querySelector(`[data-pcm-prev="${sliderId}"]`);
	const nextBtn = root.querySelector(`[data-pcm-next="${sliderId}"]`);
	if (!prevBtn || !nextBtn) {
		return;
	}
	if (!pcmThumbsNeedNav(slideTotal, cfg)) {
		prevBtn.classList.add('swiper-button-disabled');
		nextBtn.classList.add('swiper-button-disabled');
		prevBtn.setAttribute('disabled', '');
		nextBtn.setAttribute('disabled', '');
		return;
	}
	const spv = pcmLogicalThumbSpv(slideTotal, cfg);
	const minT = getMinThumbIndex(thumbs.slides.length, spv);
	const atMin = thumbs.activeIndex <= minT;
	const atMax = thumbs.isEnd;

	prevBtn.classList.toggle('swiper-button-disabled', atMin);
	nextBtn.classList.toggle('swiper-button-disabled', atMax);
	prevBtn.toggleAttribute('disabled', atMin);
	nextBtn.toggleAttribute('disabled', atMax);
}

/**
 * @param {HTMLElement | null} root
 * @param {Swiper} thumbs
 * @param {boolean} enableNav
 * @param {number} slideTotal
 * @param {Record<string, unknown>} cfg
 */
function toggleNavLocked(root, thumbs, enableNav, slideTotal, cfg) {
	if (!enableNav || !root) {
		return;
	}
	const hideNav = !pcmThumbsNeedNav(slideTotal, cfg) || Boolean(thumbs?.isLocked);
	root.classList.toggle('wp-block-wps-blocks-product-content-mentions--nav-locked', hideNav);
}

/**
 * @param {HTMLElement} pairEl
 */
function tearDownPcmSwiper(pairEl) {
	const s = pairEl._pcmSwipers;
	try {
		s?.main?.destroy?.(true, true);
		s?.thumbs?.destroy?.(true, true);
	} catch {
		// ignore
	}
	pairEl._pcmSwipers = null;
}

/**
 * @param {HTMLElement} pairEl
 * @param {HTMLElement} root
 */
function teardownPcmMobile(pairEl, root) {
	root.classList.remove('wp-block-wps-blocks-product-content-mentions--is-mobile-list');
	pairEl.querySelectorAll('.swiper-slide.is-pcm-mobile-hidden').forEach((el) => el.classList.remove('is-pcm-mobile-hidden'));
	pairEl._pcmMobileVisible = undefined;
}

/**
 * @param {HTMLElement} pairEl
 * @param {HTMLElement} root
 */
function initPcmMobileList(pairEl, root) {
	const cfg = parseConfig(pairEl.dataset.pcmConfig);
	if (!cfg || typeof cfg.sliderId !== 'string') {
		return;
	}
	tearDownPcmSwiper(pairEl);
	root.classList.add('wp-block-wps-blocks-product-content-mentions--is-mobile-list');

	const thumbsEl = pairEl.querySelector(`[data-pcm-thumbs="${cfg.sliderId}"]`);
	const slides = thumbsEl ? [...thumbsEl.querySelectorAll('.swiper-slide')] : [];
	const total = slides.length;
	let visible = Math.min(PCM_MOBILE_INITIAL, total);
	pairEl._pcmMobileVisible = visible;

	const btn = root.querySelector('[data-pcm-load-more]');
	const apply = () => {
		slides.forEach((slide, i) => {
			slide.classList.toggle('is-pcm-mobile-hidden', i >= visible);
		});
		if (btn instanceof HTMLButtonElement) {
			btn.hidden = total <= PCM_MOBILE_INITIAL || visible >= total;
		}
	};
	apply();
}

/**
 * @param {HTMLElement} root
 */
function syncPcmBlockLayout(root) {
	const pairEl = root.querySelector('[data-pcm-config]');
	if (!(pairEl instanceof HTMLElement)) {
		return;
	}
	if (isPcmMobileViewport()) {
		teardownPcmMobile(pairEl, root);
		tearDownPcmSwiper(pairEl);
		initPcmMobileList(pairEl, root);
	} else {
		teardownPcmMobile(pairEl, root);
		tearDownPcmSwiper(pairEl);
		const btn = root.querySelector('[data-pcm-load-more]');
		if (btn instanceof HTMLButtonElement) {
			btn.hidden = true;
		}
		initPcmDesktopSliders(pairEl);
	}
}

/**
 * @param {HTMLElement} pairEl
 */
function initPcmDesktopSliders(pairEl) {
	const cfg = parseConfig(pairEl.dataset.pcmConfig);
	if (!cfg || typeof cfg.sliderId !== 'string') {
		return;
	}

	tearDownPcmSwiper(pairEl);

	const root = pairEl.closest('.wp-block-wps-blocks-product-content-mentions');
	const mainEl = pairEl.querySelector(`[data-pcm-main="${cfg.sliderId}"]`);
	const thumbsEl = pairEl.querySelector(`[data-pcm-thumbs="${cfg.sliderId}"]`);
	if (!mainEl || !thumbsEl) {
		return;
	}

	const enableNav = Boolean(cfg.enableNav);

	const slideTotal = thumbsEl.querySelectorAll('.swiper-slide').length;

	const logicalSpvInit = pcmLogicalThumbSpv(slideTotal, cfg);
	const initialThumb = getMinThumbIndex(slideTotal, logicalSpvInit);

	const transitionMs = 400;

	/** @type {import('swiper/types').SwiperOptions} */
	const mainOpts = {
		slidesPerView: 1,
		spaceBetween: 16,
		speed: transitionMs,
		allowTouchMove: false,
		watchOverflow: true,
		loop: false,
	};

	/** @type {import('swiper/types').SwiperOptions} */
	const thumbsOpts = {
		initialSlide: initialThumb,
		slidesPerView: 1,
		spaceBetween: 10,
		speed: transitionMs,
		allowTouchMove: enableNav,
		watchOverflow: true,
		loop: false,
	};

	thumbsOpts.breakpoints = {
		782: {
			slidesPerView: 'auto',
			spaceBetween: 10,
		},
	};

	if (enableNav && root) {
		const prevEl = root.querySelector(`[data-pcm-prev="${cfg.sliderId}"]`);
		const nextEl = root.querySelector(`[data-pcm-next="${cfg.sliderId}"]`);
		thumbsOpts.modules = [Navigation];
		thumbsOpts.navigation = {
			prevEl: prevEl || `[data-pcm-prev="${cfg.sliderId}"]`,
			nextEl: nextEl || `[data-pcm-next="${cfg.sliderId}"]`,
		};
	}

	const mainSwiper = new Swiper(mainEl, mainOpts);
	const thumbsSwiper = new Swiper(thumbsEl, thumbsOpts);
	pairEl._pcmSwipers = { main: mainSwiper, thumbs: thumbsSwiper };

	const applyThumbSlideBounds = () => {
		if (!pcmThumbsNeedNav(slideTotal, cfg)) {
			thumbsSwiper.allowSlidePrev = false;
			thumbsSwiper.allowSlideNext = false;
			return;
		}
		const spv = pcmLogicalThumbSpv(slideTotal, cfg);
		const minT = getMinThumbIndex(slideTotal, spv);
		thumbsSwiper.allowSlidePrev = thumbsSwiper.activeIndex > minT;
		thumbsSwiper.allowSlideNext = !thumbsSwiper.isEnd;
	};

	const patchNavOverrides = () => {
		thumbsSwiper.navigation?.update?.();
		requestAnimationFrame(() => {
			applyThumbSlideBounds();
			updatePCMNavButtons(thumbsSwiper, root, cfg.sliderId, enableNav, slideTotal, cfg);
			toggleNavLocked(root, thumbsSwiper, enableNav, slideTotal, cfg);
		});
	};

	const refreshPcmPair = () => {
		syncMainFromThumbs(thumbsSwiper, mainSwiper, 0);
		applyThumbSlideBounds();
		patchNavOverrides();
	};

	thumbsSwiper.on('slideChange', () => {
		pcmPauseVideos(root);
		syncMainFromThumbs(thumbsSwiper, mainSwiper, getPcmTransitionMs(thumbsSwiper));
	});

	thumbsSwiper.on('slideChangeTransitionEnd', () => {
		syncMainFromThumbs(thumbsSwiper, mainSwiper, 0);
		applyThumbSlideBounds();
		patchNavOverrides();
		setTimeout(() => {
			updatePCMNavButtons(thumbsSwiper, root, cfg.sliderId, enableNav, slideTotal, cfg);
		}, 0);
	});

	thumbsSwiper.on('breakpoint', () => {
		const spv = pcmLogicalThumbSpv(slideTotal, cfg);
		const minT = getMinThumbIndex(slideTotal, spv);
		if (thumbsSwiper.activeIndex < minT) {
			thumbsSwiper.slideTo(minT, 0);
		}
		syncMainFromThumbs(thumbsSwiper, mainSwiper, 0);
		applyThumbSlideBounds();
		patchNavOverrides();
	});

	thumbsSwiper.on('resize', refreshPcmPair);
	thumbsSwiper.on('observerUpdate', patchNavOverrides);

	mainSwiper.on('resize', () => {
		toggleNavLocked(root, thumbsSwiper, enableNav, slideTotal, cfg);
	});

	queueMicrotask(refreshPcmPair);

	if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
		window.matchMedia('(min-width: 782px)').addEventListener('change', (e) => {
			queueMicrotask(() => {
				if (!pairEl._pcmSwipers?.thumbs) {
					return;
				}
				const { thumbs: thumbsSwiper2 } = pairEl._pcmSwipers;
				const spv = pcmLogicalThumbSpv(slideTotal, cfg);
				const minT = getMinThumbIndex(slideTotal, spv);
				if (thumbsSwiper2.activeIndex < minT) {
					thumbsSwiper2.slideTo(minT, 0);
				} else if (!e.matches && thumbsSwiper2.activeIndex > 0) {
					thumbsSwiper2.slideTo(0, 0);
				}
				thumbsSwiper2.update();
				refreshPcmPair();
			});
		});
	}
}

// —— Видео-карточки (тот же UX, что block-posts-grid), только внутри блокаmentions ——

/**
 * Ставит на паузу видео-карточки внутри scope (или во всех блоках, если scope не передан).
 * Если передан excludeVideo — его не трогаем (текущее воспроизведение).
 *
 * @param {Element | null} [scope]
 * @param {HTMLVideoElement | null} [excludeVideo]
 */
function pcmPauseVideos(scope, excludeVideo) {
	const container = scope || document;
	container.querySelectorAll('.posts-grid-card--video .posts-grid-card__video').forEach((el) => {
		if (!(el instanceof HTMLVideoElement) || el === excludeVideo) {
			return;
		}
		el.pause();
		const card = el.closest('.posts-grid-card--video');
		if (card) {
			pcmSetVideoPlaying(card, false);
		}
	});
}

/**
 * @param {HTMLElement} card
 * @param {boolean} playing
 */
function pcmSetVideoPlaying(card, playing) {
	const video = card.querySelector('.posts-grid-card__video');
	if (!(video instanceof HTMLVideoElement)) {
		return;
	}
	if (playing) {
		card.classList.add('is-playing');
		video.controls = true;
		video.muted = false;
	} else {
		card.classList.remove('is-playing');
		video.controls = false;
	}
}

const pcmVideoUiBound = new WeakSet();

/**
 * @param {HTMLVideoElement} video
 */
function pcmBindVideoUi(video) {
	if (!(video instanceof HTMLVideoElement) || pcmVideoUiBound.has(video)) {
		return;
	}
	pcmVideoUiBound.add(video);
	const card = video.closest('.posts-grid-card--video');
	if (!card) {
		return;
	}

	video.addEventListener('play', () => {
		pcmPauseVideos(null, video);
		pcmSetVideoPlaying(card, true);
	});
	video.addEventListener('pause', () => {
		pcmSetVideoPlaying(card, false);
	});
	video.addEventListener('ended', () => {
		pcmSetVideoPlaying(card, false);
	});
}

/**
 * @param {Element} root
 */
function initVideoCardsIn(root) {
	root.querySelectorAll('.posts-grid-card--video .posts-grid-card__video').forEach((v) => {
		if (v instanceof HTMLVideoElement) {
			pcmBindVideoUi(v);
		}
	});
}

document.addEventListener(
	'click',
	(event) => {
		const btn = event.target instanceof Element ? event.target.closest('.wp-block-wps-blocks-product-content-mentions .posts-grid-card__play') : null;
		if (!(btn instanceof HTMLButtonElement)) {
			return;
		}
		const card = btn.closest('.posts-grid-card--video');
		if (!card) {
			return;
		}
		const root = btn.closest('.wp-block-wps-blocks-product-content-mentions');
		if (!root) {
			return;
		}
		const video = card.querySelector('.posts-grid-card__video');
		if (!(video instanceof HTMLVideoElement)) {
			return;
		}
		event.preventDefault();
		pcmBindVideoUi(video);
		pcmPauseVideos(null, video);
		pcmSetVideoPlaying(card, true);
		video.muted = false;
		video.play().catch(() => {
			pcmSetVideoPlaying(card, false);
		});
	},
	true
);

document.addEventListener(
	'click',
	(event) => {
		const target = event.target;
		const videoEl = target instanceof Element ? target.closest('.wp-block-wps-blocks-product-content-mentions .posts-grid-card--video.is-playing .posts-grid-card__video') : null;
		if (!(videoEl instanceof HTMLVideoElement)) {
			return;
		}
		if (videoEl.closest('.posts-grid-card__play')) {
			return;
		}
		const card = videoEl.closest('.posts-grid-card--video');
		if (!card?.classList.contains('is-playing')) {
			return;
		}
		const rect = videoEl.getBoundingClientRect();
		const strip = 56;
		if (event instanceof MouseEvent && event.clientY > rect.bottom - strip) {
			return;
		}
		event.preventDefault();
		videoEl.pause();
	},
	true
);

document.addEventListener('click', (event) => {
	const btn = event.target instanceof Element ? event.target.closest('[data-pcm-load-more]') : null;
	if (!(btn instanceof HTMLButtonElement)) {
		return;
	}
	const root = btn.closest('.wp-block-wps-blocks-product-content-mentions');
	const pairEl = root?.querySelector('[data-pcm-config]');
	if (!(root instanceof HTMLElement) || !(pairEl instanceof HTMLElement)) {
		return;
	}
	if (!isPcmMobileViewport() || !root.classList.contains('wp-block-wps-blocks-product-content-mentions--is-mobile-list')) {
		return;
	}
	const cfg = parseConfig(pairEl.dataset.pcmConfig);
	if (!cfg || typeof cfg.sliderId !== 'string') {
		return;
	}
	const thumbsEl = pairEl.querySelector(`[data-pcm-thumbs="${cfg.sliderId}"]`);
	const slides = thumbsEl ? [...thumbsEl.querySelectorAll('.swiper-slide')] : [];
	const total = slides.length;
	let visible = pairEl._pcmMobileVisible ?? PCM_MOBILE_INITIAL;
	visible = Math.min(total, visible + PCM_MOBILE_STEP);
	pairEl._pcmMobileVisible = visible;
	slides.forEach((slide, i) => {
		slide.classList.toggle('is-pcm-mobile-hidden', i >= visible);
	});
	btn.hidden = total <= PCM_MOBILE_INITIAL || visible >= total;
	initVideoCardsIn(root);
});

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.wp-block-wps-blocks-product-content-mentions').forEach((root) => {
		if (root instanceof HTMLElement) {
			syncPcmBlockLayout(root);
			initVideoCardsIn(root);
		}
	});
	if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
		window.matchMedia(`(max-width: ${PCM_MOBILE_MAX}px)`).addEventListener('change', () => {
			document.querySelectorAll('.wp-block-wps-blocks-product-content-mentions').forEach((root) => {
				if (root instanceof HTMLElement) {
					syncPcmBlockLayout(root);
				}
			});
		});
	}
});
